import { useInfiniteQuery } from "react-query";
import { reactQueryGet } from "@/react/helpers/backendRequestHelpers";
import type { PaginationHelper } from "@/react/types/InternalAPIHelper";
import { internalApi } from "@circle-react/helpers/urlHelpers/internalApi";

const PER_PAGE = 50;

interface EntityPreview {
  avatar: string;
  avatar_url: string;
  bio: string;
  browser_notifications_enabled: boolean;
  comments_count: number;
  first_name: string;
  headline: string;
  hidden_space_group_ids: number[];
  home_page_posts_sort: string;
  id: number;
  is_admin: boolean;
  is_moderator: boolean;
  last_name: string;
  messaging_enabled: boolean;
  name: string;
  preferences: string;
  profile_confirmed_at: string;
  profile_info: string;
  public_uid: string;
  roles: string[];
  time_zone: string;
}

export const useEntityPreviewsApi = (
  spaceIds: number[],
  spaceGroupIds: number[],
  memberTagIds: number[],
  memberIds: number[],
) => {
  const fetchPage = (page: number) =>
    reactQueryGet<PaginationHelper<EntityPreview>>(
      internalApi.entityPreviews.index({
        params: {
          page,
          spaces_ids: spaceIds,
          space_groups_ids: spaceGroupIds,
          member_tags_ids: memberTagIds,
          members_ids: memberIds,
        },
      }),
    );

  const {
    isLoading,
    isFetching,
    data,
    refetch,
    fetchNextPage,
    fetchPreviousPage,
  } = useInfiniteQuery<PaginationHelper<EntityPreview>, Error>(
    ["entities-preview", spaceIds, spaceGroupIds, memberTagIds, memberIds],
    ({ pageParam = 1 }) => fetchPage(pageParam),
    {
      getNextPageParam: lastPage =>
        lastPage.has_next_page ? lastPage.page + 1 : undefined,
    },
  );

  const records = data?.pages?.reduce<EntityPreview[]>(
    (prev, page) => prev.concat(page.records),
    [],
  );

  const lastPage: PaginationHelper<EntityPreview> = data?.pages[
    data?.pages.length - 1
  ] || {
    page: 0,
    count: 0,
    page_count: 0,
    has_next_page: false,
    records: [],
    per_page: PER_PAGE,
  };
  const page = lastPage.page;
  const totalRecordCount = lastPage.count;
  const pageRecordCount = lastPage.page_count;
  const pageCount = totalRecordCount
    ? Math.ceil(totalRecordCount / PER_PAGE)
    : 0;
  const hasNextPage = !!lastPage.has_next_page;
  const startRecordNumber = (page - 1) * PER_PAGE + 1;
  const endRecordNumber = startRecordNumber + pageRecordCount - 1;

  return {
    page,
    records,
    totalRecordCount,
    pageRecordCount,
    pageCount,
    hasNextPage,
    startRecordNumber,
    endRecordNumber,
    isLoading,
    isFetching,
    refetch,
    fetchNextPage,
    fetchPrevPage: fetchPreviousPage,
  };
};
